import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Chip,
  Checkbox,
  SearchBar,
  Select,
  Toast,
  type Column,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { LATAM_SCOPE_ID, getPublicationIdsForScope } from "@legal-500/discovery/data";
import { SearchableSelect } from "@/components/Form/SearchableSelect";
import {
  AsyncSearchableMultiSelect,
  AsyncSearchableSelect,
  type AsyncSelectOption,
} from "@/components/Form/AsyncSearchableSelect";
import { firms as firmsService } from "@/api/firms.service";
import { contacts as contactsService } from "@/api/contacts.service";
import { PageHeader } from "@/components/PageHeader";
import { CreateTestTestimonialDialog } from "@/features/developer-tools";
import { DataTable } from "@/components/DataTable/DataTable";
import { PaginationControls } from "@/components/PaginationControls";
import EmptyState from "../../../components/EmptyState";
import LoadingState from "../../../components/LoadingState";
import { UnauthorizedView } from "@/components/UnauthorizedView";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useFilters } from "@/hooks/useFilters";
import { usePermissions } from "@/hooks/usePermissions";
import { useUrlParams } from "@/hooks/useUrlParams";
import { usePublications } from "@/hooks/usePublications";
import { useLanguages } from "@/hooks/useLanguages";
import { useApprovalRequests } from "@/hooks/useApprovalRequests";
import { useApprove, useReject, useSuspend } from "@/hooks/useApprovalActions";
import { BulkResultModal, type BulkResultAction } from "@/components/Modal/BulkResultModal";
import { LinkContactModal } from "./components/LinkContactModal";
import { BulkActionsBar } from "./components/BulkActionsBar";
import { useBulkDecision } from "./useBulkDecision";
import { useLock, useUpdateContentOutput } from "@/hooks/useLock";
import { editorialCategories } from "@/api/editorialCategories.service";
import type { LegacyPublication } from "@/api/legacy-publications.service";
import type { ApprovalRequest } from "@/api/approvalRequests.service";
import { effectiveSearchTerm, isSelectable } from "./utils";
import { contentTypeOptions } from "../contentTypeOptions";
import { CheckboxColumn } from "./components/CheckboxColumn";
import { ContentColumn } from "./components/ContentColumn";
import { DetailsColumn } from "./components/DetailsColumn";
import { pipelineService, type UnlinkTestimonialType } from "@/api/pipeline.service";
import { TESTIMONIALS_UNLINK_LAWYER_PERMISSION } from "@/constants/permissions";
import { StatusColumn } from "./components/StatusColumn";
import styles from "./ContentInbox.module.css";

const ROW_DECISION_TOAST: Record<BulkResultAction, { success: string; failure: string }> = {
  approve: { success: "Request approved successfully.", failure: "Failed to approve request" },
  reject: { success: "Request rejected successfully.", failure: "Failed to reject request" },
  suspend: { success: "Request suspended successfully.", failure: "Failed to suspend request" },
};

const ONLY_BOOKERS_EDITION = "2027";
const LATAM_PUBLICATION_IDS = getPublicationIdsForScope([LATAM_SCOPE_ID]);
const FILTER_LABELS = { onlyBookers: "Only bookers" };
const MAX_FIRMS = 200;
const FIRM_REF_SEPARATOR = ",";
const FIRM_LABEL_SEPARATOR = "|";

function decodeFirmLabel(label: string): string {
  try {
    return decodeURIComponent(label);
  } catch {
    return label;
  }
}

function parseFirmParams(firm: string | null, firmLabel: string | null): AsyncSelectOption[] {
  if (!firm || firm === "all") return [];
  const labels = firmLabel?.split(FIRM_LABEL_SEPARATOR).map(decodeFirmLabel) ?? [];
  return firm
    .split(FIRM_REF_SEPARATOR)
    .map((value, index) => ({ value, label: labels[index] || value }));
}

function ContentInboxPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { searchParams, updateUrl } = useUrlParams();
  const currentYear = new Date().getFullYear();
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [linkingContactItem, setLinkingContactItem] = useState<ApprovalRequest | null>(null);
  const [itemsPerPage, setItemsPerPage] = useState(Number(searchParams.get("pageSize")) || 10);
  const [editingRows, setEditingRows] = useState<Set<string>>(new Set());
  const [lockErrors, setLockErrors] = useState<Map<string, Error>>(new Map());
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const { hasPermission } = usePermissions();
  const { mutate: lock, loading: locking } = useLock();
  const { mutate: approve } = useApprove();
  const { mutate: reject } = useReject();
  const { mutate: suspend } = useSuspend();
  const rowDecisions: Record<BulkResultAction, (id: string) => Promise<unknown>> = {
    approve,
    reject,
    suspend,
  };
  const { mutate: saveContentOutput, loading: saving } = useUpdateContentOutput();
  const [filters, setFilters] = useState({
    edition: searchParams.get("edition") || String(currentYear),
    publication: searchParams.get("publication") || "all",
    region: searchParams.get("region") || "all",
    practice: searchParams.get("practice") || "all",
    firm: searchParams.get("firm") || "all",
    contact: searchParams.get("contact") || "all",
    status: searchParams.get("status") || "NEW",
    type: searchParams.get("type") || "all",
    sentiment: searchParams.get("sentiment") || "all",
    langInput: searchParams.get("langInput") || "all",
    langOutput: searchParams.get("langOutput") || "all",
    onlyUnlocked: searchParams.get("onlyUnlocked") !== "false",
    onlyBookers: searchParams.get("onlyBookers") === "true",
    search: searchParams.get("search") || "",
  });
  const search = effectiveSearchTerm(useDebouncedValue(filters.search));
  const urlSearch = effectiveSearchTerm(searchParams.get("search") || "");
  useEffect(() => {
    if (search === urlSearch) return;
    updateUrl({ search: search ?? "", page: "" });
  }, [search]);
  const [regionOptionsState, setRegionOptionsState] = useState<{ value: string; label: string }[]>([
    { value: "all", label: "Select publication first" },
  ]);
  const [practiceOptionsState, setPracticeOptionsState] = useState<
    { value: string; label: string }[]
  >([{ value: "all", label: "Select region first" }]);
  const [noRegionsForPublication, setNoRegionsForPublication] = useState(false);

  const { activeFilters, handleFilterChange, handleClearFilter } = useFilters({
    filters,
    setFilters,
    updateUrl,
    labelOverrides: FILTER_LABELS,
  });

  const publicationId =
    filters.publication === "all" ? undefined : Number.parseInt(filters.publication, 10);

  const showOnlyBookers =
    filters.edition === ONLY_BOOKERS_EDITION &&
    publicationId !== undefined &&
    LATAM_PUBLICATION_IDS.includes(publicationId);
  const onlyBookers = showOnlyBookers && filters.onlyBookers;

  const regionId =
    filters.publication === "all" || filters.region === "all"
      ? undefined
      : Number.parseInt(filters.region, 10);

  const practiceAreaId =
    filters.publication === "all" ||
    filters.practice === "all" ||
    (filters.region === "all" && !noRegionsForPublication)
      ? undefined
      : Number.parseInt(filters.practice, 10);

  const { data: publicationsData } = usePublications({
    edition: filters.edition,
  });

  const [selectedFirms, setSelectedFirms] = useState<AsyncSelectOption[]>(() =>
    parseFirmParams(searchParams.get("firm"), searchParams.get("firmLabel")),
  );
  const contactParam = searchParams.get("contact");
  const [selectedContactName, setSelectedContactName] = useState<string | null>(
    contactParam && contactParam !== "all" ? contactParam : null,
  );
  const handleFirmSearch = useCallback(async (query: string) => {
    const companies = await firmsService.getAllBySearch(query);
    return companies
      .filter(c => c.firmRef != null)
      .map(c => ({ value: c.firmRef as string, label: c.name }));
  }, []);

  const handleContactSearch = useCallback(async (query: string) => {
    const result = await contactsService.getAll({ search: query, limit: 50 });
    return result.contacts.map(c => ({ value: c.name, label: c.name }));
  }, []);

  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);

  // Ticks only ever refer to rows on the current page, so a different page, page size or
  // filter set starts with nothing selected.
  const listQueryKey = JSON.stringify({
    currentPage,
    itemsPerPage,
    filters: { ...filters, search },
  });
  useEffect(() => {
    setSelectedRows(prev => (prev.size === 0 ? prev : new Set()));
  }, [listQueryKey]);

  const {
    data: approvalRequestsData,
    loading,
    error,
    refetch: refetchApprovalRequests,
  } = useApprovalRequests({
    limit: itemsPerPage,
    offset: (currentPage - 1) * itemsPerPage,
    publicationId,
    regionId,
    practiceAreaId,
    edition: filters.edition !== String(currentYear) ? filters.edition : undefined,
    firmRef: filters.firm !== "all" ? filters.firm : undefined,
    lawyerName: filters.contact !== "all" ? filters.contact : undefined,
    status: filters.status !== "all" ? filters.status : undefined,
    contentType: filters.type !== "all" ? filters.type : undefined,
    sentiment: filters.sentiment !== "all" ? filters.sentiment : undefined,
    langInput: filters.langInput !== "all" ? filters.langInput : undefined,
    langOutput: filters.langOutput !== "all" ? filters.langOutput : undefined,
    onlyUnlocked: filters.onlyUnlocked || undefined,
    onlyBookers: onlyBookers || undefined,
    search,
  });

  const approvalRequests = useMemo(() => {
    return approvalRequestsData?.approvalRequests || [];
  }, [approvalRequestsData?.approvalRequests]);

  const pagination = approvalRequestsData?.pagination || {
    total: 0,
    limit: itemsPerPage,
    offset: 0,
    hasMore: false,
  };

  // Load regions based on publication
  useEffect(() => {
    if (filters.publication === "all") {
      setRegionOptionsState([{ value: "all", label: "Select publication first" }]);
      setNoRegionsForPublication(false);
      return;
    }

    const loadRegions = async () => {
      try {
        const resp = await editorialCategories.getAll({
          publicationId,
          type: "country",
          limit: 100,
        });
        const options =
          resp.editorialCategories?.map(cat => ({
            value: String(cat.id),
            label:
              (cat as unknown as { country?: string }).country ||
              (cat as unknown as { name?: string }).name ||
              `Region ${cat.id}`,
          })) ?? [];
        if (options.length === 0) {
          setNoRegionsForPublication(true);
        } else {
          setNoRegionsForPublication(false);
        }
        setRegionOptionsState([{ value: "all", label: "All regions" }, ...options]);
      } catch (_e) {
        setRegionOptionsState([{ value: "all", label: "Failed to load regions" }]);
      }
    };

    loadRegions();
  }, [filters.publication, publicationId]);

  useEffect(() => {
    if (filters.publication === "all" || (filters.region === "all" && !noRegionsForPublication)) {
      setPracticeOptionsState([{ value: "all", label: "Select region first" }]);
      return;
    }

    const loadPracticeAreas = async () => {
      try {
        // Page through all practice areas (backend caps `limit` at 100 per request).
        const allPracticeAreas = await editorialCategories.getAllPracticeAreas({
          publicationId,
          ...(noRegionsForPublication ? {} : { countryId: regionId }),
        });
        const options = allPracticeAreas
          .map((cat, index) => {
            const ec = cat as unknown as {
              id: number;
              practiceGroup?: string;
              practiceArea?: string;
              tableName?: string;
              name?: string;
            };

            const parts: string[] = [];
            if (ec.practiceGroup) parts.push(ec.practiceGroup);
            if (ec.practiceArea) parts.push(ec.practiceArea);
            if (ec.tableName) parts.push(ec.tableName);

            // Remove consecutive duplicates, e.g. "Corporate > Corporate"
            const deduped: string[] = [];
            for (const part of parts) {
              if (deduped.length === 0 || deduped[deduped.length - 1] !== part) {
                deduped.push(part);
              }
            }

            let label: string;
            if (deduped.length === 0) {
              label = ec.name || `Practice ${ec.id}`;
            } else if (deduped.length === 1) {
              label = deduped[0];
            } else {
              label = deduped.join(" > ");
            }

            return {
              value: `${ec.id}:${index}`,
              label,
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label));

        setPracticeOptionsState([{ value: "all", label: "All practices" }, ...options]);
      } catch (_e) {
        setPracticeOptionsState([{ value: "all", label: "Failed to load practices" }]);
      }
    };

    loadPracticeAreas();
  }, [filters.publication, filters.region, publicationId, regionId, noRegionsForPublication]);

  const handleSelectRow = (idOrAll: string | "all", selected: boolean) => {
    if (idOrAll === "all") {
      if (selected) {
        const selectableIds = approvalRequests
          .filter(item => isSelectable(item.status))
          .map(item => item.id);
        setSelectedRows(new Set(selectableIds));
      } else {
        setSelectedRows(new Set());
      }
    } else {
      setSelectedRows(prev => {
        const newSet = new Set(prev);
        if (selected) {
          newSet.add(idOrAll);
        } else {
          newSet.delete(idOrAll);
        }
        return newSet;
      });
    }
  };

  const removeFromSelection = (id: string) => {
    setSelectedRows(prev => {
      if (!prev.has(id)) return prev;
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  };

  const bulk = useBulkDecision({
    approvalRequests,
    selectedRows,
    editingRows,
    refetch: refetchApprovalRequests,
    clearSelection: () => setSelectedRows(new Set()),
    onError: message => setToast({ message, type: "error" }),
  });

  const handleRowDecision = async (action: BulkResultAction, id: string) => {
    try {
      await rowDecisions[action](id);
      removeFromSelection(id);
      await refetchApprovalRequests();
      setToast({ message: ROW_DECISION_TOAST[action].success, type: "success" });
    } catch (error) {
      const err = (error instanceof Error ? error : new Error(String(error))) as Error & {
        status?: number;
      };

      if (err.status === 409) {
        setLockErrors(prev => new Map(prev).set(id, err));
        return;
      }

      const errorMessage = err.message || ROW_DECISION_TOAST[action].failure;
      setToast({
        message: `Request failed: ${errorMessage}`,
        type: "error",
      });
    }
  };

  const canUnlinkLawyer = (row: ApprovalRequest) =>
    hasPermission(TESTIMONIALS_UNLINK_LAWYER_PERMISSION) &&
    (row.contactId != null || !!row.lawyerName);

  const handleUnlinkLawyer = async (
    row: ApprovalRequest,
    testimonialType: UnlinkTestimonialType | undefined,
  ) => {
    try {
      await pipelineService.unlinkLawyerFromProcessingItem(row.externalId, testimonialType);
      await refetchApprovalRequests();
      setToast({ message: "Lawyer removed from testimonial", type: "success" });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to remove lawyer";
      setToast({ message: `Request failed: ${errorMessage}`, type: "error" });
    }
  };

  const handleApprove = (id: string) => handleRowDecision("approve", id);
  const handleReject = (id: string) => handleRowDecision("reject", id);
  const handleSuspend = (id: string) => handleRowDecision("suspend", id);

  const handleSave = async (id: string, contentOutput: string) => {
    try {
      await saveContentOutput(id, contentOutput);
      setEditingRows(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      await refetchApprovalRequests();
      setToast({
        message: "Changes saved successfully.",
        type: "success",
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to save changes for this request";
      setToast({
        message: `Request failed: ${errorMessage}`,
        type: "error",
      });
    }
  };

  const handleEdit = async (id: string) => {
    try {
      await lock(id);
      setEditingRows(prev => new Set(prev).add(id));
      setLockErrors(prev => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      setLockErrors(prev => new Map(prev).set(id, err));
    }
  };

  const handleEditCancel = (id: string) => {
    setEditingRows(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setLockErrors(prev => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  };

  const handleOpenLinkModal = useCallback((item: ApprovalRequest) => {
    setLinkingContactItem(item);
  }, []);

  const editionOptions = useMemo(() => {
    const years: { value: string; label: string }[] = [];
    for (let year = currentYear + 1; year >= 2017; year--) {
      years.push({ value: String(year), label: String(year) });
    }
    return years;
  }, [currentYear]);

  const publicationOptions = useMemo(() => {
    const base = [{ value: "all", label: "All publications" }];
    const pubs = publicationsData?.publications ?? [];
    return [
      ...base,
      ...pubs.map((p: LegacyPublication) => ({
        value: String(p.id),
        label: p.publication
          ? `${p.publication} (${p.edition ?? "n/a"})`
          : `Publication ${p.id}${p.edition ? ` (${p.edition})` : ""}`,
      })),
    ];
  }, [publicationsData?.publications]);

  const regionOptions = useMemo(() => {
    return regionOptionsState;
  }, [regionOptionsState]);

  const practiceOptions = useMemo(() => {
    return practiceOptionsState;
  }, [practiceOptionsState]);

  const statusOptions = useMemo(
    () => [
      { value: "all", label: "All Statuses" },
      { value: "NEW", label: "New" },
      { value: "APPROVED", label: "Approved" },
      { value: "REJECTED", label: "Rejected" },
      { value: "SUSPENDED", label: "Suspended" },
      { value: "WITHDRAWN", label: "Withdrawn" },
    ],
    [],
  );

  const sentimentOptions = useMemo(
    () => [
      { value: "all", label: "All sentiments" },
      { value: "positive", label: "Positive" },
      { value: "negative", label: "Negative" },
      { value: "neutral", label: "Neutral" },
    ],
    [],
  );

  const { data: languagesData } = useLanguages(filters.edition);

  const langInputOptions = useMemo(() => {
    const langs = languagesData?.langInputs ?? [];
    return [
      { value: "all", label: "All Input Languages" },
      ...langs.sort().map(lang => ({ value: lang, label: lang.split("-")[0].toUpperCase() })),
    ];
  }, [languagesData?.langInputs]);

  const langOutputOptions = useMemo(() => {
    const langs = languagesData?.langOutputs ?? [];
    return [
      { value: "all", label: "All Output Languages" },
      ...langs.sort().map(lang => ({ value: lang, label: lang.split("-")[0].toUpperCase() })),
    ];
  }, [languagesData?.langOutputs]);

  const handleSearchChange = (value: string) => {
    if (effectiveSearchTerm(value) !== effectiveSearchTerm(filters.search)) setCurrentPage(1);
    setFilters(prev => ({ ...prev, search: value }));
  };

  const handleFilterChangeWithDeps = (key: string, value: string | number | boolean) => {
    setCurrentPage(1);
    const stringValue = String(value);

    if (key === "edition") {
      setSelectedFirms([]);
      setSelectedContactName(null);
      setFilters(prev => ({
        ...prev,
        edition: stringValue,
        publication: "all",
        region: "all",
        practice: "all",
        firm: "all",
        contact: "all",
        onlyBookers: false,
      }));
      updateUrl({
        edition: stringValue,
        publication: "all",
        region: "all",
        practice: "all",
        firm: "all",
        firmLabel: "",
        contact: "all",
        onlyBookers: "",
        page: "",
      });
      return;
    }

    if (key === "publication") {
      setNoRegionsForPublication(false);
      setSelectedFirms([]);
      setSelectedContactName(null);
      setFilters(prev => ({
        ...prev,
        publication: stringValue,
        region: "all",
        practice: "all",
        firm: "all",
        contact: "all",
        onlyBookers: false,
      }));
      updateUrl({
        publication: stringValue,
        region: "all",
        practice: "all",
        firm: "all",
        firmLabel: "",
        contact: "all",
        onlyBookers: "",
        page: "",
      });
      return;
    }

    if (key === "region") {
      if (filters.publication === "all") {
        setFilters(prev => ({ ...prev, region: "all" }));
        updateUrl({ region: "all", page: "" });
      } else {
        setSelectedFirms([]);
        setSelectedContactName(null);
        setFilters(prev => ({
          ...prev,
          region: stringValue,
          practice: "all",
          firm: "all",
          contact: "all",
        }));
        updateUrl({
          region: stringValue,
          practice: "all",
          firm: "all",
          firmLabel: "",
          contact: "all",
          page: "",
        });
      }
      return;
    }

    if (key === "practice") {
      if (filters.publication === "all" || (filters.region === "all" && !noRegionsForPublication)) {
        setFilters(prev => ({ ...prev, practice: "all" }));
        updateUrl({ practice: "all", page: "" });
      } else {
        setSelectedFirms([]);
        setSelectedContactName(null);
        setFilters(prev => ({
          ...prev,
          practice: stringValue,
          firm: "all",
          contact: "all",
        }));
        updateUrl({ practice: stringValue, firm: "all", firmLabel: "", contact: "all", page: "" });
      }
      return;
    }

    handleFilterChange(key, value, { page: "" });
  };

  const handleFirmsChange = (firms: AsyncSelectOption[]) => {
    setCurrentPage(1);
    setSelectedFirms(firms);
    setSelectedContactName(null);
    const firm =
      firms.length === 0 ? "all" : firms.map(f => String(f.value)).join(FIRM_REF_SEPARATOR);
    setFilters(prev => ({ ...prev, firm, contact: "all" }));
    updateUrl({
      firm,
      firmLabel: firms.map(f => encodeURIComponent(f.label)).join(FIRM_LABEL_SEPARATOR),
      contact: "all",
      page: "",
    });
  };

  const handleClearFilterWithDeps = (key: string) => {
    setCurrentPage(1);

    if (key === "edition") {
      const editionValue = String(currentYear);
      setSelectedFirms([]);
      setSelectedContactName(null);
      setFilters(prev => ({
        ...prev,
        edition: editionValue,
        publication: "all",
        region: "all",
        practice: "all",
        firm: "all",
        contact: "all",
        onlyBookers: false,
      }));
      updateUrl({
        edition: editionValue,
        publication: "all",
        region: "all",
        practice: "all",
        firm: "all",
        firmLabel: "",
        contact: "all",
        onlyBookers: "",
        page: "",
      });
      return;
    }

    if (key === "publication") {
      setNoRegionsForPublication(false);
      setSelectedFirms([]);
      setSelectedContactName(null);
      setFilters(prev => ({
        ...prev,
        publication: "all",
        region: "all",
        practice: "all",
        firm: "all",
        contact: "all",
        onlyBookers: false,
      }));
      updateUrl({
        publication: "all",
        region: "all",
        practice: "all",
        firm: "all",
        firmLabel: "",
        contact: "all",
        onlyBookers: "",
        page: "",
      });
      return;
    }

    if (key === "region") {
      setSelectedFirms([]);
      setSelectedContactName(null);
      setFilters(prev => ({
        ...prev,
        region: "all",
        practice: "all",
        firm: "all",
        contact: "all",
      }));
      updateUrl({
        region: "all",
        practice: "all",
        firm: "all",
        firmLabel: "",
        contact: "all",
        page: "",
      });
      return;
    }

    if (key === "practice") {
      setSelectedFirms([]);
      setSelectedContactName(null);
      setFilters(prev => ({ ...prev, practice: "all", firm: "all", contact: "all" }));
      updateUrl({ practice: "all", firm: "all", firmLabel: "", contact: "all", page: "" });
      return;
    }

    if (key.startsWith("firm:")) {
      const firmRef = key.slice("firm:".length);
      handleFirmsChange(selectedFirms.filter(firm => String(firm.value) !== firmRef));
      return;
    }

    if (key === "contact") {
      setSelectedContactName(null);
      handleFilterChange("contact", "all", { page: "" });
      return;
    }

    if (key === "search") {
      handleSearchChange("");
      return;
    }

    handleClearFilter?.(key, { page: "" });
  };

  const FILTER_CONFIGS_ROW_1 = [
    {
      key: "edition",
      label: "Edition",
      options: editionOptions,
      preserveOptionOrder: true,
    },
    {
      key: "publication",
      label: "Publication",
      options: publicationOptions,
    },
    {
      key: "region",
      label: "Region",
      options: regionOptions,
    },
    {
      key: "practice",
      label: "Practice",
      options: practiceOptions,
    },
  ];

  const FILTER_CONFIGS_ROW_2 = [
    {
      key: "status",
      label: "Status",
      options: statusOptions,
    },
    {
      key: "sentiment",
      label: "Sentiment",
      options: sentimentOptions,
    },
    {
      key: "langInput",
      label: "Input Language",
      options: langInputOptions,
    },
    {
      key: "langOutput",
      label: "Output Language",
      options: langOutputOptions,
    },
  ];

  const enhancedActiveFilters = useMemo(() => {
    return activeFilters.flatMap(filter => {
      if (filter.key === "search") {
        return search === undefined ? [] : [{ ...filter, displayValue: search } as const];
      }
      if (filter.key === "publication") {
        const match = publicationOptions.find(opt => String(opt.value) === filters.publication);
        return {
          ...filter,
          displayValue: match?.label ?? String(filter.value),
        } as const;
      }
      if (filter.key === "region") {
        const match = regionOptions.find(opt => String(opt.value) === filters.region);
        return {
          ...filter,
          displayValue: match?.label ?? String(filter.value),
        } as const;
      }
      if (filter.key === "practice") {
        const match = practiceOptions.find(opt => String(opt.value) === filters.practice);
        return {
          ...filter,
          displayValue: match?.label ?? String(filter.value),
        } as const;
      }
      if (filter.key === "firm") {
        return selectedFirms.map(
          firm =>
            ({
              key: `firm:${firm.value}`,
              label: filter.label,
              value: String(firm.value),
              displayValue: firm.label,
            }) as const,
        );
      }
      if (filter.key === "onlyBookers") {
        return onlyBookers ? [filter] : [];
      }
      if (filter.key === "contact") {
        return {
          ...filter,
          displayValue: selectedContactName ?? String(filter.value),
        } as const;
      }
      return [{ ...filter, displayValue: filter.displayValue ?? String(filter.value) } as const];
    });
  }, [
    activeFilters,
    search,
    filters.publication,
    filters.region,
    filters.practice,
    filters.firm,
    filters.contact,
    publicationOptions,
    regionOptions,
    practiceOptions,
    selectedFirms,
    selectedContactName,
    onlyBookers,
  ]);

  const allSelected = useMemo(() => {
    const selectableIds = approvalRequests
      .filter(item => isSelectable(item.status))
      .map(item => item.id);
    return selectableIds.length > 0 && selectableIds.every(id => selectedRows.has(id));
  }, [selectedRows, approvalRequests]);

  const someSelected = useMemo(() => {
    const selectableIds = approvalRequests
      .filter(item => isSelectable(item.status))
      .map(item => item.id);
    const selectedCount = selectableIds.filter(id => selectedRows.has(id)).length;
    return selectedCount > 0 && selectedCount < selectableIds.length;
  }, [selectedRows, approvalRequests]);

  const columns: Column<ApprovalRequest>[] = useMemo(
    () => [
      {
        header: (
          <CheckboxColumn
            isSelected={allSelected}
            isIndeterminate={someSelected}
            onSelect={handleSelectRow}
          />
        ) as unknown as string,
        accessor: "id" as keyof ApprovalRequest,
        render: (_value: unknown, row: ApprovalRequest) => (
          <CheckboxColumn
            item={row}
            isSelected={selectedRows.has(row.id)}
            onSelect={handleSelectRow}
          />
        ),
      },
      {
        header: "CONTENT",
        accessor: "contentOutput" as keyof ApprovalRequest,
        render: (_value: unknown, row: ApprovalRequest) => (
          <ContentColumn
            item={row}
            onApprove={handleApprove}
            onReject={handleReject}
            onSuspend={handleSuspend}
            isEditing={editingRows.has(row.id)}
            onEditStart={handleEdit}
            onEditCancel={handleEditCancel}
            onSave={handleSave}
            saving={saving}
            lockError={lockErrors.get(row.id) || null}
            locking={locking}
          />
        ),
      },
      {
        header: "DETAILS",
        accessor: "publicationName" as keyof ApprovalRequest,
        render: (_value: unknown, row: ApprovalRequest) => (
          <DetailsColumn
            item={row}
            onLinkContact={() => handleOpenLinkModal(row)}
            onUnlinkLawyer={
              canUnlinkLawyer(row) ? type => handleUnlinkLawyer(row, type) : undefined
            }
            unlinkDisabled={bulk.running !== null}
          />
        ),
      },
      {
        header: "STATUS",
        accessor: "status" as keyof ApprovalRequest,
        render: (_value: unknown, row: ApprovalRequest) => <StatusColumn item={row} />,
      },
    ],
    [
      selectedRows,
      allSelected,
      someSelected,
      handleApprove,
      handleReject,
      handleSuspend,
      handleEdit,
      handleEditCancel,
      handleSave,
      handleSelectRow,
      editingRows,
      lockErrors,
      locking,
      saving,
      handleOpenLinkModal,
      canUnlinkLawyer,
      handleUnlinkLawyer,
      bulk.running,
    ],
  );

  if (error) {
    const status = (error as Error & { status?: number }).status;
    const isAuthError = status === 401 || status === 403;
    if (isAuthError) {
      return <UnauthorizedView />;
    }
  }

  return (
    <div className={styles.wrapper}>
      <div>
        <PageHeader title="Testimonials" />
        {hasPermission("developer.job_runs") && (
          <CreateTestTestimonialDialog onSuccess={() => refetchApprovalRequests()} />
        )}
      </div>

      <div className={styles.filtersContainer}>
        <div className={styles.filtersRow}>
          {FILTER_CONFIGS_ROW_1.map(filter => {
            let className = styles.filterDropdown;
            if (filter.key === "edition") {
              className = `${styles.filterDropdown} ${styles.filterDropdownSmall}`;
            } else if (filter.key === "publication") {
              className = `${styles.filterDropdown} ${styles.filterDropdownWide}`;
            } else if (filter.key === "practice") {
              className = `${styles.filterDropdown} ${styles.filterDropdownPractice}`;
            }
            const filterKey = filter.key as keyof typeof filters;
            const filterValue = filters[filterKey];
            return (
              <div key={filter.key} className={className}>
                <SearchableSelect
                  name={filter.key}
                  label={filter.label}
                  value={
                    typeof filterValue === "string" || typeof filterValue === "number"
                      ? filterValue
                      : String(filterValue)
                  }
                  options={filter.options}
                  preserveOptionOrder={
                    "preserveOptionOrder" in filter && filter.preserveOptionOrder
                  }
                  onChange={value => handleFilterChangeWithDeps(filter.key, value ?? "all")}
                />
              </div>
            );
          })}
        </div>
        <div className={styles.filtersRow}>
          <div className={styles.filterDropdown}>
            <AsyncSearchableMultiSelect
              name="firm"
              label="Firm"
              value={selectedFirms}
              onChange={handleFirmsChange}
              onSearch={handleFirmSearch}
              placeholder="Search firms..."
              minSearchLength={1}
              maxSelected={MAX_FIRMS}
            />
          </div>
          <div className={styles.filterDropdown}>
            <AsyncSearchableSelect
              name="contact"
              label="Contact"
              value={filters.contact === "all" ? null : filters.contact}
              onChange={value => {
                if (value == null) setSelectedContactName(null);
                handleFilterChangeWithDeps("contact", value != null ? String(value) : "all");
              }}
              onOptionChange={option => setSelectedContactName(option?.label ?? null)}
              onSearch={handleContactSearch}
              placeholder="Search contacts..."
              minSearchLength={1}
              initialOption={
                filters.contact !== "all"
                  ? { value: filters.contact, label: filters.contact }
                  : null
              }
            />
          </div>
          {/* Content type filter rendered directly after Contact */}
          <div className={styles.filterDropdown}>
            <SearchableSelect
              name="type"
              label="Content type"
              value={filters.type}
              options={contentTypeOptions}
              onChange={value => handleFilterChangeWithDeps("type", value ?? "all")}
            />
          </div>
          {FILTER_CONFIGS_ROW_2.map(filter => {
            const filterKey = filter.key as keyof typeof filters;
            const filterValue = filters[filterKey];
            return (
              <div key={filter.key} className={styles.filterDropdown}>
                <SearchableSelect
                  name={filter.key}
                  label={filter.label}
                  value={
                    typeof filterValue === "string" || typeof filterValue === "number"
                      ? filterValue
                      : String(filterValue)
                  }
                  options={filter.options}
                  onChange={value => handleFilterChangeWithDeps(filter.key, value ?? "all")}
                />
              </div>
            );
          })}
          <div className={styles.filterDropdown}>
            <SearchBar
              value={filters.search}
              onChange={handleSearchChange}
              onClear={() => handleSearchChange("")}
              placeholder="Search content..."
            />
          </div>
          <div className={styles.filterCheckbox}>
            <Checkbox
              checked={filters.onlyUnlocked}
              onChange={e => {
                const checked = e.target.checked;
                setFilters(prev => ({ ...prev, onlyUnlocked: checked }));
                if (checked) {
                  updateUrl({ onlyUnlocked: "true" });
                } else {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete("onlyUnlocked");
                  updateUrl({ onlyUnlocked: "" });
                }
              }}
              label="Show only unlocked"
            />
          </div>
          {showOnlyBookers && (
            <div className={styles.filterCheckbox}>
              <Checkbox
                checked={filters.onlyBookers}
                aria-label="Only bookers"
                onChange={e => {
                  const checked = e.target.checked;
                  setFilters(prev => ({ ...prev, onlyBookers: checked }));
                  updateUrl({ onlyBookers: checked ? "true" : "" });
                }}
                label="Only bookers"
              />
            </div>
          )}
        </div>
      </div>

      <div className={styles.filtersAndPaginationRow}>
        {enhancedActiveFilters.length > 0 && handleClearFilterWithDeps && (
          <div className={styles.activeFilters}>
            <span className={styles.activeFiltersLabel}>Active filters:</span>
            <div className={styles.activeFiltersChips}>
              {enhancedActiveFilters.map(filter => (
                <Chip
                  key={filter.key}
                  size="small"
                  variant="outlined"
                  className={styles.filterChip}
                  onDelete={() => handleClearFilterWithDeps(filter.key)}
                >
                  {typeof filters[filter.key as keyof typeof filters] === "boolean"
                    ? filter.label
                    : `${filter.label}: ${filter.displayValue || filter.value}`}
                </Chip>
              ))}
            </div>
          </div>
        )}
        <div className={styles.rowsPerPageControl}>
          <label htmlFor="itemsPerPage" className={styles.rowsPerPageLabel}>
            Rows per page:
          </label>
          <Select
            id="itemsPerPage"
            value={itemsPerPage}
            onChange={e => {
              const newPageSize = Number(e.target.value);
              setItemsPerPage(newPageSize);
              updateUrl({ pageSize: String(newPageSize) });
            }}
            options={[
              { value: 10, label: "10" },
              { value: 25, label: "25" },
              { value: 50, label: "50" },
              { value: 100, label: "100" },
            ]}
            size="small"
          />
        </div>
      </div>

      <BulkActionsBar
        selectedCount={bulk.selectedCount}
        counts={bulk.counts}
        blockedReason={bulk.blockedReason}
        running={bulk.running}
        onRun={bulk.run}
        onClearSelection={() => setSelectedRows(new Set())}
      />

      {loading && <LoadingState message="Loading content..." />}

      {error && !loading && (
        <EmptyState
          variant="error"
          title="Failed to load content"
          message={error.message}
          action={{
            label: "Retry",
            onClick: () => window.location.reload(),
          }}
        />
      )}

      {!loading && !error && (
        <>
          <div className={styles.tableContainer}>
            <div className={styles.scopedTable}>
              <DataTable
                data={approvalRequests}
                columns={columns}
                emptyMessage="No content found matching your criteria."
                onRowClick={row => {
                  navigate(`/research/content-inbox/${row.id}`, {
                    state: { returnSearch: location.search },
                  });
                }}
              />
            </div>
          </div>

          {pagination.total > 0 && (
            <PaginationControls
              currentPage={currentPage}
              totalPages={Math.ceil(pagination.total / itemsPerPage)}
              startIndex={pagination.offset + 1}
              endIndex={Math.min(pagination.offset + approvalRequests.length, pagination.total)}
              totalCount={pagination.total}
              itemName="items"
              onPageChange={page => {
                setCurrentPage(page);
                updateUrl({ page: String(page) });
              }}
            />
          )}
        </>
      )}

      <BulkResultModal {...bulk.result} onClose={bulk.closeResult} />

      {linkingContactItem && (
        <LinkContactModal
          item={linkingContactItem}
          onClose={() => setLinkingContactItem(null)}
          onLinked={() => {
            setLinkingContactItem(null);
            refetchApprovalRequests();
          }}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={toast.type === "error" ? 5000 : 3000}
        />
      )}
    </div>
  );
}

export default ContentInboxPage;
